import { useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useProfilePicture } from "@miplatform/hooks";
import {
  createUUID,
  externalReferenceId,
  getRootUrl,
  sortOnField,
} from "@miplatform/utils";

const getUserUUID = createUUID();
const updateUserUUID = createUUID();

export function useUserDetailsViewModel() {
  const [user, setUser] = useState(null);
  const stateManager = useComponentState();

  const getUser = (id) => {
    stateManager.abort(getUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.get(id);
      if (response.status === HTTP_STATUSES.OK) {
        setUser({
          ...response.data,
          roleIds: response.data.roles
            .sort(sortOnField("name"))
            .map((role) => role.id),

          // this is added bc the response.data does not have the title when user does not have title and it causes isDirty to be true
          title: response.data.title || "",
        });
        return response.data;
      }
      return null;
    }, getUserUUID);
  };

  const updateUser = (id, data) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.updateUser({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `User updated successfully!`,
          },
          failure: { text: `Failed to update user.` },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        return response.data;
      }
      return null;
    }, getUserUUID);
  };

  const activateUser = (id, data) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const { status } = await subscriptionService.users.activateUser({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `User activated successfully!`,
          },
        },
      });
      if (status === HTTP_STATUSES.OK) {
        return status;
      }
      return null;
    }, getUserUUID);
  };

  const deactivateUser = (id, data) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const { status } = await subscriptionService.users.deactivateUser({
        id,
        data,
        toastMessageConfig: {
          success: {
            text: `User disabled successfully!`,
          },
        },
      });
      if (status === HTTP_STATUSES.OK) {
        return status;
      }
      return null;
    }, getUserUUID);
  };

  // Photo functions
  const {
    isLoading: profilePictureLoading,
    removePicture,
    uploadPicture,
  } = useProfilePicture({
    values: user,
    getExternalReferenceId: () =>
      user
        ? externalReferenceId.getProfilePhotoRefId({
            userId: user.id,
          })
        : null,
  });

  const getProfilePhotoUrl = (documentId) => {
    if (!documentId) return null;
    return `${getRootUrl()}/document-service/documents/${documentId}`;
  };

  const uploadProfilePhoto = async (file) => {
    await deleteProfilePhoto();

    const response = await uploadPicture(file);
    if (response.status === HTTP_STATUSES.OK) {
      // Refresh user data to get the updated photo
      await getUser(user.id);
    }
    return response;
  };

  const deleteProfilePhoto = async () => {
    if (!user?.photo?.id) {
      return null;
    }
    const response = await removePicture();
    if (response.status === HTTP_STATUSES.OK) {
      // Refresh user data to reflect the deleted photo
      await getUser(user.id);
    }
    return response;
  };

  return {
    user,
    updateUser,
    getUser,
    activateUser,
    deactivateUser,
    stateManager,
    isLoading: stateManager.isBusy() || profilePictureLoading,
    // Photo functions
    uploadProfilePhoto,
    deleteProfilePhoto,
    getProfilePhotoUrl,
  };
}
